import type { IsEqual } from "type-fest";
import type { infer as InferZodType, ZodSchema } from "zod";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function assertTypeMatch<_T extends true>(_?: any): asserts _ {}

export { assertTypeMatch };

type ExcludeFromTuple<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends any[] | readonly any[],
    U,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
> = T extends readonly any[]
    ? T extends readonly [infer F, ...infer R]
        ? F extends U
            ? ExcludeFromTuple<R, U>
            : readonly [F, ...ExcludeFromTuple<R, U>]
        : []
    : T extends [infer F, ...infer R]
      ? F extends U
          ? ExcludeFromTuple<R, U>
          : [F, ...ExcludeFromTuple<R, U>]
      : [];

export type { ExcludeFromTuple };

/**
 * Asserts that type `T` extends type `U`. If `T` extends `U`, the type resolves to `U`, otherwise it resolves to `never`.
 *
 * @template T - The type to be checked.
 * @template U - The type to check against.
 */
type AssertType<T, U> = T extends U ? U : never;

/**
 * Assert that type A (Subset) is a subset of type B (Superset)
 * E.g.
 * type A = "ett" | "två";
 * type B = "ett" | "två" | "tre";
 * type ValidExample = AssertSubset<A, B>; // This passes
 * type InvalidExample = AssertSubset<B, A>; // This fails
 */
type AssertSubset<Subset extends Superset, Superset> = Subset;
export type { AssertSubset, AssertType };

/**
 * Assert that type A does not equal to type B
 */
type IsNotEqual<A, B> = IsEqual<A, B> extends true ? false : true;
assertTypeMatch<IsNotEqual<1, 2>>(); // 1 !== 2
assertTypeMatch<IsEqual<IsNotEqual<1, 1>, false>>(); // 1 === 1
export type { IsNotEqual };

/**
 * Infers the TypeScript type from a given Zod schema.
 * @template FormSchema - The Zod schema from which to infer the type.
 */
type InferTypeFromSchema<Schema extends ZodSchema> = InferZodType<Schema>;

export type { InferTypeFromSchema };
